/**
 * @nanokart/tryon-studio-sdk
 * Try-On API Service
 * Calls POST /api/v1/tryonstudio/tryon
 */

import { ApiClient } from './client';
import { TryOnResult } from '../../types/config';

export interface TryOnStudioApiResponse {
  request_id: string;
  operation_id: string;
  status: string;
  tryon_key: string;       // S3 key — pass to Moments API
  image_url: string;       // Try-on result image URL
  result_image_url: string; // Alias for image_url
  credits_consumed: number;
  cached: boolean;
  latency_ms: number;
}

/**
 * TryOn API Service for the TryOn Studio SDK
 */
export class TryOnApiService {
  constructor(private client: ApiClient) {}

  /**
   * Generate a virtual try-on.
   * Uploads the human photo + passes garment URL to /api/v1/tryonstudio/tryon.
   * When `garmentImage` (File) is provided — e.g. a live camera capture —
   * it is uploaded instead of a catalog URL.
   */
  async generateTryOn(
    humanImage: File,
    garmentImageUrl: string,
    skuId?: string,
    customerId?: string,
    garmentImage?: File
  ): Promise<TryOnStudioApiResponse> {
    const formData = new FormData();
    formData.append('human_image', humanImage);
    if (garmentImage) {
      formData.append('garment_image', garmentImage);
    } else {
      formData.append('garment_image_url', garmentImageUrl);
    }
    if (skuId) formData.append('sku_id', skuId);
    if (customerId) formData.append('customer_id', customerId);

    const response = await this.client.postForm<TryOnStudioApiResponse>(
      '/api/v1/tryonstudio/tryon',
      formData
    );
    return response.data;
  }

  /**
   * Map API response to SDK TryOnResult
   */
  static mapToTryOnResult(
    response: TryOnStudioApiResponse,
    productId: string,
    inputPhotoPreview: string,
    garmentImageUrl: string
  ): TryOnResult {
    return {
      cacheKey: response.tryon_key,
      productId,
      resultImageUrl: response.result_image_url || response.image_url,
      inputPhotoUrl: inputPhotoPreview,
      garmentImageUrl,
      processingTimeMs: response.latency_ms ?? 0,
      timestamp: new Date().toISOString(),
    };
  }
}
